import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, AlertTriangle, Shield, Trash2, Edit } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Risk {
  id: string;
  projectId: string;
  riskNumber: string;
  title: string;
  description: string | null;
  category: string;
  status: string;
  likelihood: string;
  impact: string;
  riskScore: number;
  mitigationPlan: string | null;
  contingencyPlan: string | null;
  riskOwnerName: string | null;
  identifiedDate: string;
  targetResolution: string | null;
  resolvedDate: string | null;
  projectName: string;
  linkedRequirementCount: number;
}

interface Project {
  id: string;
  name: string;
}

const categoryLabels: Record<string, string> = {
  technical: 'Technical',
  schedule: 'Schedule',
  budget: 'Budget',
  resource: 'Resource',
  scope: 'Scope',
  compliance: 'Compliance',
  external: 'External',
};

const statusLabels: Record<string, string> = {
  identified: 'Identified',
  analyzing: 'Analyzing',
  mitigating: 'Mitigating',
  resolved: 'Resolved',
  accepted: 'Accepted',
  closed: 'Closed',
};

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  identified: 'warning',
  analyzing: 'info',
  mitigating: 'default',
  resolved: 'success',
  accepted: 'secondary',
  closed: 'secondary',
};

const likelihoodLabels: Record<string, string> = {
  very_low: 'Very Low',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  very_high: 'Very High',
};

function getRiskScoreColor(score: number) {
  if (score >= 16) return 'bg-red-600 text-white';
  if (score >= 12) return 'bg-orange-500 text-white';
  if (score >= 6) return 'bg-yellow-500 text-white';
  if (score >= 3) return 'bg-blue-500 text-white';
  return 'bg-green-500 text-white';
}

function getRiskScoreLabel(score: number) {
  if (score >= 16) return 'Critical';
  if (score >= 12) return 'High';
  if (score >= 6) return 'Medium';
  if (score >= 3) return 'Low';
  return 'Minimal';
}

export function RisksPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const initialProject = searchParams.get('project') || '';
  const [selectedProjectId, setSelectedProjectId] = useState<string>(initialProject);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingRisk, setEditingRisk] = useState<Risk | null>(null);
  const [form, setForm] = useState({
    projectId: '',
    riskNumber: '',
    title: '',
    description: '',
    category: 'technical',
    likelihood: 'medium',
    impact: 'medium',
    mitigationPlan: '',
    contingencyPlan: '',
    targetResolution: '',
    status: 'identified',
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-dropdown'],
    queryFn: async () => {
      const r = await api.listProjects({ limit: 100 });
      return (r.data || []) as Project[];
    },
  });
  const projects = projectsData || [];

  const { data: risksData, isLoading } = useQuery({
    queryKey: ['risks', selectedProjectId, statusFilter, categoryFilter, search],
    queryFn: async () => {
      const r = await api.listRisks({
        projectId: selectedProjectId || undefined,
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        search: search || undefined,
        limit: 100,
      });
      return (r.data || []) as Risk[];
    },
  });
  const risks = risksData || [];

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createRisk(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
      setShowCreate(false);
      toast({ title: 'Risk created' });
    },
    onError: () => toast({ title: 'Failed to create risk', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.updateRisk(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
      setEditingRisk(null);
      toast({ title: 'Risk updated' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteRisk(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
      toast({ title: 'Risk deleted' });
    },
  });

  const handleCreate = () => {
    createMutation.mutate({
      projectId: form.projectId || selectedProjectId,
      riskNumber: form.riskNumber,
      title: form.title,
      description: form.description || null,
      category: form.category,
      likelihood: form.likelihood,
      impact: form.impact,
      mitigationPlan: form.mitigationPlan || null,
      contingencyPlan: form.contingencyPlan || null,
      targetResolution: form.targetResolution || null,
    });
  };

  const handleUpdate = () => {
    if (!editingRisk) return;
    updateMutation.mutate({
      id: editingRisk.id,
      data: {
        title: form.title,
        description: form.description || null,
        category: form.category,
        status: form.status,
        likelihood: form.likelihood,
        impact: form.impact,
        mitigationPlan: form.mitigationPlan || null,
        contingencyPlan: form.contingencyPlan || null,
        targetResolution: form.targetResolution || null,
      },
    });
  };

  const openEdit = (risk: Risk) => {
    setForm({
      projectId: risk.projectId,
      riskNumber: risk.riskNumber,
      title: risk.title,
      description: risk.description || '',
      category: risk.category,
      likelihood: risk.likelihood,
      impact: risk.impact,
      mitigationPlan: risk.mitigationPlan || '',
      contingencyPlan: risk.contingencyPlan || '',
      targetResolution: risk.targetResolution || '',
      status: risk.status,
    });
    setEditingRisk(risk);
  };

  // Summary stats
  const openRisks = risks.filter((r) => !['resolved', 'closed', 'accepted'].includes(r.status));
  const highRisks = openRisks.filter((r) => r.riskScore >= 12);
  const avgScore = openRisks.length > 0 ? (openRisks.reduce((sum, r) => sum + r.riskScore, 0) / openRisks.length).toFixed(1) : '0';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Risk Register</h1>
          <p className="text-muted-foreground">Identify, assess, and mitigate project risks</p>
        </div>
        <Button onClick={() => { setForm({ projectId: selectedProjectId, riskNumber: '', title: '', description: '', category: 'technical', likelihood: 'medium', impact: 'medium', mitigationPlan: '', contingencyPlan: '', targetResolution: '', status: 'identified' }); setShowCreate(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Add Risk
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Risks</CardTitle>
            <AlertTriangle className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{openRisks.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High/Critical</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{highRisks.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Risk Score</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgScore}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Risks</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{risks.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select value={selectedProjectId || '__all__'} onValueChange={(v) => setSelectedProjectId(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Projects</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter || '__all__'} onValueChange={(v) => setStatusFilter(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Statuses</SelectItem>
            {Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={categoryFilter || '__all__'} onValueChange={(v) => setCategoryFilter(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Categories</SelectItem>
            {Object.entries(categoryLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-[200px]" />
      </div>

      {/* Risk Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : risks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No risks found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[90px]">ID</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Likelihood</TableHead>
                  <TableHead>Impact</TableHead>
                  <TableHead className="text-center">Score</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {risks.map((risk) => (
                  <TableRow key={risk.id}>
                    <TableCell className="font-mono text-xs">{risk.riskNumber}</TableCell>
                    <TableCell>
                      <div className="font-medium">{risk.title}</div>
                      {risk.mitigationPlan && (
                        <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[250px]">
                          Mitigation: {risk.mitigationPlan}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{risk.projectName}</TableCell>
                    <TableCell><Badge variant="secondary">{categoryLabels[risk.category]}</Badge></TableCell>
                    <TableCell className="text-sm">{likelihoodLabels[risk.likelihood]}</TableCell>
                    <TableCell className="text-sm">{likelihoodLabels[risk.impact]}</TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${getRiskScoreColor(risk.riskScore)}`}>
                        {risk.riskScore}
                      </span>
                      <div className="text-[10px] text-muted-foreground">{getRiskScoreLabel(risk.riskScore)}</div>
                    </TableCell>
                    <TableCell><Badge variant={statusVariant[risk.status] || 'secondary'}>{statusLabels[risk.status]}</Badge></TableCell>
                    <TableCell className="text-sm">{risk.riskOwnerName || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(risk)}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(risk.id)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Risk Heat Map */}
      {openRisks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Risk Heat Map</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-6 gap-1 text-xs">
              <div></div>
              {['Very Low', 'Low', 'Medium', 'High', 'Very High'].map((label) => (
                <div key={label} className="text-center font-medium py-1">{label}</div>
              ))}
              {['very_high', 'high', 'medium', 'low', 'very_low'].map((likelihood, li) => (
                <React.Fragment key={likelihood}>
                  <div className="font-medium py-2 text-right pr-2">{likelihoodLabels[likelihood]}</div>
                  {['very_low', 'low', 'medium', 'high', 'very_high'].map((impact, ii) => {
                    const score = (5 - li) * (ii + 1);
                    const count = openRisks.filter((r) => r.likelihood === likelihood && r.impact === impact).length;
                    return (
                      <div
                        key={`${likelihood}-${impact}`}
                        className={`text-center py-2 rounded text-xs font-medium ${
                          score >= 16 ? 'bg-red-100 text-red-800' :
                          score >= 12 ? 'bg-orange-100 text-orange-800' :
                          score >= 6 ? 'bg-yellow-100 text-yellow-800' :
                          score >= 3 ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}
                      >
                        {count > 0 ? count : ''}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
              <div></div>
              <div className="col-span-5 text-center text-muted-foreground mt-1">Impact &rarr;</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate || !!editingRisk} onOpenChange={(open) => { if (!open) { setShowCreate(false); setEditingRisk(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRisk ? 'Edit Risk' : 'Add Risk'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editingRisk && (
              <div>
                <Label>Project</Label>
                <Select value={form.projectId} onValueChange={(v) => setForm({ ...form, projectId: v })}>
                  <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {!editingRisk && (
                <div>
                  <Label>Risk #</Label>
                  <Input value={form.riskNumber} onChange={(e) => setForm({ ...form, riskNumber: e.target.value })} placeholder="RISK-001" />
                </div>
              )}
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {editingRisk && (
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Likelihood</Label>
                <Select value={form.likelihood} onValueChange={(v) => setForm({ ...form, likelihood: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(likelihoodLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Impact</Label>
                <Select value={form.impact} onValueChange={(v) => setForm({ ...form, impact: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(likelihoodLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Mitigation Plan</Label>
              <Textarea value={form.mitigationPlan} onChange={(e) => setForm({ ...form, mitigationPlan: e.target.value })} rows={2} />
            </div>
            <div>
              <Label>Contingency Plan</Label>
              <Textarea value={form.contingencyPlan} onChange={(e) => setForm({ ...form, contingencyPlan: e.target.value })} rows={2} />
            </div>
            <div>
              <Label>Target Resolution Date</Label>
              <Input type="date" value={form.targetResolution} onChange={(e) => setForm({ ...form, targetResolution: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditingRisk(null); }}>Cancel</Button>
            <Button onClick={editingRisk ? handleUpdate : handleCreate} disabled={!form.title || (!editingRisk && (!form.projectId || !form.riskNumber))}>
              {editingRisk ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
